import { useEffect, useState } from "react";
import { api, asList, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Plus, Trash2, Pencil, Copy, X } from "lucide-react";

const empty = {
    code: "",
    display_name: "",
    gap_units: 1,
    stack_rank: 1,
    notes: "",
    active: true,
};

export default function HeightProfiles() {
    const [profiles, setProfiles] = useState([]);
    const [cartSlots, setCartSlots] = useState(34);
    const [maxPos, setMaxPos] = useState(35);
    const [editing, setEditing] = useState(null);
    const [error, setError] = useState("");
    const [loadError, setLoadError] = useState("");

    const load = async () => {
        setLoadError("");
        try {
            const [r, cfg] = await Promise.all([
                api.get("/height-profiles"),
                api.get("/config"),
            ]);
            setProfiles(asList(r.data, ["height_profiles", "profiles"]));
            setCartSlots(cfg.data.cart_slot_count || 34);
            setMaxPos(cfg.data.max_layer_position || (cfg.data.cart_slot_count || 34) + 1);
        } catch (e) {
            setLoadError(formatApiErrorDetail(e.response?.data?.detail ?? "Hoogteprofielen konden niet worden geladen"));
        }
    };
    useEffect(() => {
        load();
    }, []);

    const save = async () => {
        setError("");
        const g = Number(editing.gap_units);
        if (!g || g <= 0) {
            setError("Tussengaten moet groter dan 0 zijn");
            return;
        }
        try {
            const payload = {
                ...editing,
                gap_units: g,
                stack_rank: Number(editing.stack_rank),
            };
            if (editing.id) await api.put(`/height-profiles/${editing.id}`, payload);
            else await api.post("/height-profiles", payload);
            setEditing(null);
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail ?? "Opslaan mislukt"));
        }
    };
    const remove = async (p) => {
        if (!window.confirm(`Profiel ${p.code} verwijderen?`)) return;
        try {
            await api.delete(`/height-profiles/${p.id}`);
            await load();
        } catch (e) {
            alert(formatApiErrorDetail(e.response?.data?.detail ?? "Verwijderen mislukt"));
        }
    };

    return (
        <div>
            <PageHeader
                kicker="Master Data"
                title="Hoogteprofielen"
                subtitle={`Tussengaten = aantal lege kargaten tussen twee opeenvolgende platen. Een CC-kar heeft ${cartSlots} echte gaten + 1 positie bovenop (positie ${maxPos}) — formule: lagen planten = ⌊${maxPos} / tussengaten⌋, platen = lagen − 1.`}
                testid="profiles-header"
                actions={
                    <button
                        onClick={() => setEditing({ ...empty })}
                        data-testid="btn-new-profile"
                        className="px-4 py-2 rounded-sm bg-florefy-900 hover:bg-florefy-700 text-white text-sm flex items-center gap-2 transition-colors"
                    >
                        <Plus className="h-4 w-4" /> Nieuw profiel
                    </button>
                }
            />

            {loadError && (
                <div className="mb-5 flex items-center justify-between gap-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                    <span>{loadError}</span>
                    <button type="button" onClick={load} className="font-semibold underline">Opnieuw proberen</button>
                </div>
            )}

            <div className="bg-white border border-border rounded-sm overflow-x-auto">
                <table className="w-full text-sm" data-testid="profiles-table">
                    <thead>
                        <tr className="bg-ink-50">
                            <th className="text-left px-4 py-3 data-label">Code</th>
                            <th className="text-left px-4 py-3 data-label">Naam</th>
                            <th className="text-right px-4 py-3 data-label">Tussengaten</th>
                            <th className="text-right px-4 py-3 data-label">Max lagen / kar</th>
                            <th className="text-right px-4 py-3 data-label">Stapelvolgorde</th>
                            <th className="text-left px-4 py-3 data-label">Notities</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {profiles.length === 0 && (
                            <tr>
                                <td colSpan={7} className="px-5 py-10 text-center text-ink-500">
                                    Geen hoogteprofielen.
                                </td>
                            </tr>
                        )}
                        {profiles.map((p) => {
                            const maxLayers = (p.gap_units || 0) > 0
                                ? Math.floor(maxPos / p.gap_units)
                                : 0;
                            const plates = Math.max(0, maxLayers - 1);
                            return (
                            <tr
                                key={p.id}
                                className="border-t border-border/60 hover:bg-white"
                                data-testid={`profile-row-${p.code}`}
                            >
                                <td className="px-4 py-3 font-mono font-semibold">{p.code}</td>
                                <td className="px-4 py-3">{p.display_name}</td>
                                <td className="px-4 py-3 font-mono text-right">{p.gap_units}</td>
                                <td className="px-4 py-3 font-mono text-right font-semibold text-florefy-900">
                                    {maxLayers || "—"}
                                    {maxLayers > 0 && (
                                        <span className="text-ink-500 font-normal ml-1">({plates} platen)</span>
                                    )}
                                </td>
                                <td className="px-4 py-3 font-mono text-right">{p.stack_rank}</td>
                                <td className="px-4 py-3 text-ink-500 italic text-xs">
                                    {p.notes}
                                </td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <button
                                        onClick={() => {
                                            const { id: _id, ...rest } = p;
                                            setEditing({ ...rest, code: `${p.code}-KOPIE` });
                                        }}
                                        data-testid={`btn-duplicate-profile-${p.code}`}
                                        className="text-ink-700 hover:text-florefy-900 p-1"
                                        title="Dupliceer profiel"
                                    >
                                        <Copy className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => setEditing({ ...p })}
                                        data-testid={`btn-edit-profile-${p.code}`}
                                        className="text-ink-700 hover:text-florefy-900 p-1 ml-1"
                                        title="Bewerken"
                                    >
                                        <Pencil className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => remove(p)}
                                        data-testid={`btn-delete-profile-${p.code}`}
                                        className="text-ink-700 hover:text-destructive p-1 ml-1"
                                        title="Verwijderen"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </td>
                            </tr>
                        );})}
                    </tbody>
                </table>
            </div>

            {editing && (
                <div className="fixed inset-0 bg-florefy-900/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-sm w-full max-w-md p-6 mx-auto" data-testid="profile-modal">
                        <div className="flex items-center justify-between mb-4 pb-3 border-b border-border">
                            <h2 className="text-xl font-semibold text-ink-900">
                                {editing.id ? "Profiel bewerken" : "Nieuw profiel"}
                            </h2>
                            <button onClick={() => setEditing(null)}>
                                <X className="h-5 w-5 text-ink-700" />
                            </button>
                        </div>
                        {error && (
                            <div className="bg-destructive/10 border border-destructive text-destructive text-sm p-3 mb-3 rounded-sm">
                                {error}
                            </div>
                        )}
                        <div className="space-y-3">
                            <FieldP label="Code">
                                <input
                                    className={inputCls}
                                    value={editing.code}
                                    onChange={(e) => setEditing({ ...editing, code: e.target.value })}
                                    data-testid="input-profile-code"
                                />
                            </FieldP>
                            <FieldP label="Weergavenaam">
                                <input
                                    className={inputCls}
                                    value={editing.display_name}
                                    onChange={(e) => setEditing({ ...editing, display_name: e.target.value })}
                                    data-testid="input-profile-name"
                                />
                            </FieldP>
                            <div className="grid grid-cols-2 gap-3">
                                <FieldP label="Tussengaten (aantal kargaten boven plaat)">
                                    <input
                                        type="number"
                                        min="0"
                                        className={inputCls}
                                        value={editing.gap_units}
                                        onChange={(e) => setEditing({ ...editing, gap_units: e.target.value })}
                                        data-testid="input-profile-gap"
                                    />
                                </FieldP>
                                <FieldP label="Stapelvolgorde (1=op, hoog=zwaar onder)">
                                    <input
                                        type="number"
                                        className={inputCls}
                                        value={editing.stack_rank}
                                        onChange={(e) => setEditing({ ...editing, stack_rank: e.target.value })}
                                        data-testid="input-profile-stack"
                                    />
                                </FieldP>
                            </div>
                            <div className="text-xs font-mono text-ink-500 bg-ink-50 p-2 rounded-sm">
                                {(() => {
                                    const g = Number(editing.gap_units) || 0;
                                    if (g <= 0) return "⚠ Tussengaten moet groter dan 0 zijn.";
                                    const ml = Math.floor(maxPos / g);
                                    const plates = Math.max(0, ml - 1);
                                    return `→ Bij ${g} tussengaten passen er ${ml} lagen planten op de kar (${plates} losse platen + 1 bovenop). Formule: ⌊${maxPos} / ${g}⌋ = ${ml}.`;
                                })()}
                            </div>
                            <FieldP label="Notities">
                                <textarea
                                    className={inputCls}
                                    rows="2"
                                    value={editing.notes}
                                    onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                                    data-testid="input-profile-notes"
                                />
                            </FieldP>
                        </div>
                        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border">
                            <button
                                onClick={() => setEditing(null)}
                                className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-ink-50"
                            >
                                Annuleer
                            </button>
                            <button
                                onClick={save}
                                className="px-4 py-2 rounded-sm bg-florefy-900 hover:bg-florefy-700 text-white text-sm font-medium"
                                data-testid="btn-save-profile"
                            >
                                Opslaan
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const inputCls =
    "w-full px-3 py-2 border border-border rounded-sm bg-white font-mono text-sm focus:outline-none focus:ring-1 focus:ring-florefy-900";

function FieldP({ label, children }) {
    return (
        <div>
            <div className="data-label mb-1">{label}</div>
            {children}
        </div>
    );
}
